import { useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { OspToast } from "@ospweb/sdk-ui";
import { QUERY_KEYS } from "@config/constant";
import type { EquityGrantCodeEnum } from "@features/api/memberApi";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import { rootStore } from "@store/rootStore";

export const useMemberGrantEquity = ({
  code,
  enabled = true,
}: {
  code: EquityGrantCodeEnum;
  enabled?: boolean;
}) => {
  const { memberApi } = useContext(OspApiClientContext);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const queryClient = useQueryClient();

  const equityGrantQueryKey = [
    QUERY_KEYS.MEMBER_EQUITY_GRANT,
    ownerInfo?.id,
    code,
  ] as const;

  const queryResult = useQuery({
    queryKey: equityGrantQueryKey,
    enabled: enabled && !!memberApi && !!ownerInfo?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await memberApi?.member?.queryEquityGrant(code);
      return res?.data?.data;
    },
  });

  const { mutateAsync: grantEquity, isPending: isGrantEquityPending } =
    useMutation({
      mutationFn: async () => {
        if (!memberApi) {
          throw new Error("memberApi is not available");
        }
        const res = await memberApi.member.grantEquity(code);
        return res?.data?.data;
      },
      onSuccess: (_data) => {
        OspToast.success({
          content: "Credits used. Successfully unlocked!",
        });
        queryClient.setQueryData(equityGrantQueryKey, () => {
          return 0;
        });
      },
    });

  return { queryResult, grantEquity, isGrantEquityPending };
};
